import React from 'react';
import { Box, Button, FormHelperText, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { WEEK_DAYS, nextSlot } from '../../constants/openingHours';

/**
 * Opening hours as a list of slots — a day, an opening and a closing time —
 * rather than a sentence. A day can have several (a lunch break is two), and a
 * day with none is a day the shop is closed.
 *
 * Controlled: `value` is the list, `onChange` receives the new one.
 */
function OpeningHoursEditor({ value, onChange, disabled = false, error = '' }) {
  const update = (index, changes) =>
    onChange(value.map((slot, i) => (i === index ? { ...slot, ...changes } : slot)));

  const remove = (index) => onChange(value.filter((_, i) => i !== index));

  const add = () => onChange([...value, nextSlot(value)]);

  return (
    <Box>
      {0 === value.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Aucun horaire renseigné.
        </Typography>
      )}

      <Stack spacing={1.5} sx={{ mb: 1.5 }}>
        {value.map((slot, index) => (
          // The list is edited in place and never reordered while typing, so
          // the position is a stable key.
          // eslint-disable-next-line react/no-array-index-key
          <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
            <TextField
              select
              size="small"
              label="Jour"
              value={slot.day}
              onChange={(e) => update(index, { day: Number(e.target.value) })}
              disabled={disabled}
              sx={{ minWidth: 140 }}
            >
              {WEEK_DAYS.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              type="time"
              label="Ouverture"
              value={slot.opens}
              onChange={(e) => update(index, { opens: e.target.value })}
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              size="small"
              type="time"
              label="Fermeture"
              value={slot.closes}
              onChange={(e) => update(index, { closes: e.target.value })}
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
            />

            <IconButton
              aria-label={`Supprimer l’horaire ${index + 1}`}
              onClick={() => remove(index)}
              disabled={disabled}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      <Button startIcon={<AddIcon />} onClick={add} disabled={disabled} size="small" variant="outlined">
        Ajouter un horaire
      </Button>

      {error && (
        <FormHelperText error sx={{ mt: 1 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
}

export default OpeningHoursEditor;
